import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const OPENING_DATE = new Date('2026-05-13T11:00:00');

const ComingSoon = ({ children }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const calculate = () => {
      const now = new Date();
      const diff = OPENING_DATE - now;

      if (diff <= 0) {
        setIsOpen(true);
        return;
      }

      setIsOpen(false);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculate();
    const timer = setInterval(calculate, 1000);
    return () => clearInterval(timer);
  }, []);

  // After opening date — show the real site
  if (isOpen) return children;

  // Before opening — show coming soon screen
  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center justify-center relative overflow-hidden px-4">

      {/* Background radial glow */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: 'radial-gradient(ellipse at 50% 40%, rgba(201,168,76,0.10) 0%, transparent 65%)',
        }}
      />

      {/* Jali pattern */}
      <div
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.04,
          backgroundImage: `repeating-linear-gradient(45deg, #C9A84C 0px, #C9A84C 1px, transparent 1px, transparent 32px),
                            repeating-linear-gradient(-45deg, #C9A84C 0px, #C9A84C 1px, transparent 1px, transparent 32px)`,
        }}
      />

      {/* Border */}
      <div style={{ position:'absolute', inset:'16px', border:'1.5px solid rgba(201,168,76,0.35)', pointerEvents:'none' }} />
      <div style={{ position:'absolute', inset:'24px', border:'0.5px solid rgba(201,168,76,0.12)', pointerEvents:'none' }} />

      {/* Corner marks */}
      {[
        { top:'28px', left:'28px', borderTop:'2px solid #C9A84C', borderLeft:'2px solid #C9A84C' },
        { top:'28px', right:'28px', borderTop:'2px solid #C9A84C', borderRight:'2px solid #C9A84C' },
        { bottom:'28px', left:'28px', borderBottom:'2px solid #C9A84C', borderLeft:'2px solid #C9A84C' },
        { bottom:'28px', right:'28px', borderBottom:'2px solid #C9A84C', borderRight:'2px solid #C9A84C' },
      ].map((s, i) => (
        <div key={i} style={{ position:'absolute', width:'24px', height:'24px', ...s, pointerEvents:'none' }} />
      ))}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 flex flex-col items-center text-center max-w-2xl w-full"
      >
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          style={{ fontFamily:'Cinzel, serif', fontSize:'10px', letterSpacing:'0.5em', color:'rgba(201,168,76,0.6)', textTransform:'uppercase', marginBottom:'16px' }}
        >
          Solihull · Est. 2012
        </motion.p>

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          style={{ marginBottom: '8px' }}
        >
          <span style={{ display:'block', fontFamily:'Georgia, serif', fontStyle:'italic', fontWeight:300, fontSize:'clamp(22px, 4vw, 32px)', color:'#C9A84C', letterSpacing:'0.06em', lineHeight:1 }}>
            It's
          </span>
          <span style={{ display:'block', fontFamily:'Cinzel, serif', fontWeight:900, fontSize:'clamp(52px, 10vw, 88px)', color:'#E8C96A', letterSpacing:'0.18em', lineHeight:0.95 }}>
            LAHORE
          </span>
        </motion.div>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{ fontFamily:'Cinzel, serif', fontSize:'9px', letterSpacing:'0.4em', color:'rgba(201,168,76,0.45)', textTransform:'uppercase', marginBottom:'32px' }}
        >
          Authentic Punjabi Cuisine
        </motion.p>

        {/* Gold rule */}
        <div style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', marginBottom:'32px' }}>
          <div style={{ flex:1, height:'0.8px', background:'linear-gradient(90deg, transparent, rgba(201,168,76,0.4))' }} />
          <span style={{ color:'#C9A84C', fontSize:'8px' }}>◆</span>
          <div style={{ flex:1, height:'0.8px', background:'linear-gradient(90deg, rgba(201,168,76,0.4), transparent)' }} />
        </div>

        {/* Coming soon headline */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          style={{ fontFamily:'Cinzel, serif', fontWeight:700, fontSize:'clamp(13px, 2.5vw, 16px)', letterSpacing:'0.35em', color:'#F2EDE4', textTransform:'uppercase', marginBottom:'8px' }}
        >
          Grand Opening
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          style={{ fontFamily:'Georgia, serif', fontStyle:'italic', fontSize:'clamp(16px, 3vw, 22px)', color:'rgba(201,168,76,0.75)', marginBottom:'40px' }}
        >
          Tuesday 13th May 2026
        </motion.p>

        {/* Countdown */}
        {timeLeft && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'12px', width:'100%', maxWidth:'480px', marginBottom:'40px' }}
          >
            {[
              { label: 'Days', value: timeLeft.days },
              { label: 'Hours', value: timeLeft.hours },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Seconds', value: timeLeft.seconds },
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  background:'rgba(201,168,76,0.06)',
                  border:'1px solid rgba(201,168,76,0.2)',
                  padding:'16px 8px',
                  textAlign:'center',
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.span
                    key={value}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    style={{ display:'block', fontFamily:'Cinzel, serif', fontWeight:700, fontSize:'clamp(28px, 5vw, 42px)', color:'#E8C96A', lineHeight:1 }}
                  >
                    {String(value).padStart(2, '0')}
                  </motion.span>
                </AnimatePresence>
                <span style={{ display:'block', fontFamily:'Cinzel, serif', fontSize:'8px', letterSpacing:'0.3em', color:'rgba(201,168,76,0.45)', textTransform:'uppercase', marginTop:'6px' }}>
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Offer banner */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          style={{
            width:'100%', maxWidth:'520px',
            background:'rgba(201,168,76,0.08)',
            border:'1px solid rgba(201,168,76,0.3)',
            padding:'20px 24px',
            marginBottom:'32px',
          }}
        >
          <p style={{ fontFamily:'Cinzel, serif', fontSize:'10px', letterSpacing:'0.4em', color:'rgba(201,168,76,0.55)', textTransform:'uppercase', marginBottom:'8px' }}>
            Opening Celebration
          </p>
          <p style={{ fontFamily:'Georgia, serif', fontStyle:'italic', fontSize:'clamp(18px, 3vw, 24px)', color:'#F2EDE4', lineHeight:1.4, marginBottom:'6px' }}>
            50% off selected items
          </p>
          <p style={{ fontFamily:'Cinzel, serif', fontSize:'10px', letterSpacing:'0.2em', color:'rgba(201,168,76,0.6)', textTransform:'uppercase' }}>
            13th & 14th May Only
          </p>
        </motion.div>

        {/* Gold rule */}
        <div style={{ width:'100%', display:'flex', alignItems:'center', gap:'12px', marginBottom:'24px' }}>
          <div style={{ flex:1, height:'0.8px', background:'linear-gradient(90deg, transparent, rgba(201,168,76,0.3))' }} />
          <span style={{ color:'#C9A84C', fontSize:'8px' }}>◆</span>
          <div style={{ flex:1, height:'0.8px', background:'linear-gradient(90deg, rgba(201,168,76,0.3), transparent)' }} />
        </div>

        {/* Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1 }}
          style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' }}
        >
          <p style={{ fontFamily:'Cinzel, serif', fontSize:'8px', letterSpacing:'0.35em', color:'rgba(201,168,76,0.4)', textTransform:'uppercase' }}>
            44 Station Road · Solihull · B91 3RX
          </p>
          <a
            href="tel:+447402766220"
            style={{ fontFamily:'Cinzel, serif', fontSize:'14px', color:'#C9A84C', letterSpacing:'0.1em', textDecoration:'none' }}
          >
            +44 7402 766 220
          </a>
          <a
            href="mailto:info@itslahore.co.uk"
            style={{ fontFamily:'Georgia, serif', fontStyle:'italic', fontSize:'12px', color:'rgba(201,168,76,0.5)', textDecoration:'none' }}
          >
            info@itslahore.co.uk
          </a>
        </motion.div>

      </motion.div>
    </div>
  );
};

export default ComingSoon;
